/**
 * VoiceStudio
 * ──────────────────────────────────────────────────────────────────────
 * Echo voice campaign voice library + live preview.
 *
 *   • Library of pre-built + custom voices (gender, accent, age, tone).
 *   • Filter chips (all / female / male, warm / clinical / neutral, etc).
 *   • Select a default voice for new voice campaigns.
 *   • Type a sentence and hit Preview to hear the browser TTS render —
 *     uses window.speechSynthesis + tries to match the voice locale. No
 *     server round-trip; works 100% in the browser.
 *   • "Clone your own voice" upsell card (disabled · stub).
 *
 * Demo-only. No real backend writes.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import {
  Play, Square, CheckCircle2, Mic, Volume2, Loader2, Sparkles, Star, UserSquare2,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAppMode } from '../../contexts/AppModeContext';

/* ── Voice catalogue. `engineHints` steer the browser's SpeechSynthesis engine
   toward a voice whose name/language matches the persona. If nothing matches,
   we fall back to the default voice and only adjust rate/pitch. ── */
const VOICES = [
  { id: 'natasha', name: 'Natasha', gender: 'female', accent: 'en-US', tone: 'warm', age: 'adult', desc: 'Warm · US · default Echo voice', engineHints: ['samantha', 'jenny', 'google us english', 'microsoft aria'], rate: 1.0, pitch: 1.05 },
  { id: 'caleb', name: 'Caleb', gender: 'male', accent: 'en-US', tone: 'neutral', age: 'adult', desc: 'Neutral · US · steady, clear', engineHints: ['daniel', 'alex', 'google us english', 'microsoft guy'], rate: 1.0, pitch: 0.95 },
  { id: 'sophia', name: 'Sophia', gender: 'female', accent: 'en-GB', tone: 'clinical', age: 'adult', desc: 'Clinical · UK · clear, measured', engineHints: ['serena', 'google uk english female', 'microsoft libby'], rate: 0.95, pitch: 1.0 },
  { id: 'oliver', name: 'Oliver', gender: 'male', accent: 'en-GB', tone: 'warm', age: 'adult', desc: 'Warm · UK · friendly, inviting', engineHints: ['oliver', 'daniel', 'google uk english male'], rate: 1.0, pitch: 0.98 },
  { id: 'maria', name: 'María', gender: 'female', accent: 'es-US', tone: 'warm', age: 'adult', desc: 'Bilingual · ES-US · warm', engineHints: ['paulina', 'google español de estados unidos', 'microsoft sabina'], rate: 1.0, pitch: 1.02 },
  { id: 'hiro', name: 'Hiro', gender: 'male', accent: 'en-US', tone: 'neutral', age: 'senior', desc: 'Senior · US · calm, reassuring', engineHints: ['fred', 'daniel', 'microsoft guy'], rate: 0.92, pitch: 0.9 },
  { id: 'zoe', name: 'Zoë', gender: 'female', accent: 'en-AU', tone: 'warm', age: 'young', desc: 'Warm · AU · younger, upbeat', engineHints: ['karen', 'google australian'], rate: 1.05, pitch: 1.1 },
  { id: 'robot', name: 'Aria Classic', gender: 'neutral', accent: 'en-US', tone: 'clinical', age: 'adult', desc: 'Neutral · US · legacy IVR feel', engineHints: ['albert', 'zarvox', 'microsoft david'], rate: 1.0, pitch: 1.0 },
];

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'female', label: 'Female', key: 'gender' },
  { id: 'male', label: 'Male', key: 'gender' },
  { id: 'warm', label: 'Warm', key: 'tone' },
  { id: 'neutral', label: 'Neutral', key: 'tone' },
  { id: 'clinical', label: 'Clinical', key: 'tone' },
  { id: 'en-US', label: 'US', key: 'accent' },
  { id: 'en-GB', label: 'UK', key: 'accent' },
  { id: 'es-US', label: 'Spanish', key: 'accent' },
];

const pickVoice = (hints = [], preferredLang = 'en-US') => {
  if (typeof window === 'undefined' || !window.speechSynthesis) return null;
  const all = window.speechSynthesis.getVoices();
  if (!all || all.length === 0) return null;
  // 1) exact hint match
  for (const h of hints) {
    const m = all.find((v) => v.name.toLowerCase().includes(h));
    if (m) return m;
  }
  // 2) language match
  const lang = all.find((v) => v.lang && v.lang.toLowerCase().startsWith(preferredLang.toLowerCase().slice(0, 2)));
  return lang || all[0];
};

const VoiceStudio = () => {
  const { isDemo } = useAppMode();
  const [filter, setFilter] = useState('all');
  const [voiceId, setVoiceId] = useState('natasha');
  const [defaultId, setDefaultId] = useState('natasha');
  const [sample, setSample] = useState(isDemo
    ? "Hi, this is Echo calling on behalf of Riverside Family Medicine to confirm your appointment tomorrow at 10 AM with Dr. Chen. Please press 1 to confirm, or 2 to reschedule."
    : "Hi, this is Echo calling on behalf of your practice to confirm your appointment. Please press 1 to confirm, or 2 to reschedule.");
  const [playingId, setPlayingId] = useState(null);
  const [voicesReady, setVoicesReady] = useState(false);

  // The browser sometimes loads voices async — re-trigger render when they show up.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const s = window.speechSynthesis;
    const check = () => {
      if (s.getVoices().length > 0) setVoicesReady(true);
    };
    check();
    s.addEventListener?.('voiceschanged', check);
    return () => s.removeEventListener?.('voiceschanged', check);
  }, []);

  const visibleVoices = useMemo(() => {
    if (filter === 'all') return VOICES;
    const match = FILTERS.find((f) => f.id === filter);
    if (!match) return VOICES;
    return VOICES.filter((v) => v[match.key] === filter);
  }, [filter]);

  const selected = VOICES.find((v) => v.id === voiceId) || VOICES[0];

  const stopAll = () => {
    try { window.speechSynthesis.cancel(); } catch { /* noop */ }
    setPlayingId(null);
  };

  const preview = (id) => {
    if (!('speechSynthesis' in window)) {
      toast.error('Your browser does not support voice preview. Try Chrome, Edge, or Safari.');
      return;
    }
    const voice = VOICES.find((v) => v.id === id) || selected;
    stopAll();
    const utter = new SpeechSynthesisUtterance(sample || 'Hello. This is a preview of the selected Echo voice.');
    const engineVoice = pickVoice(voice.engineHints, voice.accent);
    if (engineVoice) utter.voice = engineVoice;
    utter.lang = voice.accent;
    utter.rate = voice.rate;
    utter.pitch = voice.pitch;
    utter.onend = () => setPlayingId(null);
    utter.onerror = () => setPlayingId(null);
    setPlayingId(id);
    window.speechSynthesis.speak(utter);
  };

  const setAsDefault = (id) => {
    setDefaultId(id);
    toast.success(`${VOICES.find((v) => v.id === id)?.name} set as default voice for new campaigns`);
  };

  const speechAvailable = typeof window !== 'undefined' && 'speechSynthesis' in window;

  return (
    <div className="space-y-4" data-testid="voice-studio">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <p className="text-[13px] font-semibold text-slate-900 inline-flex items-center gap-1.5">
            <Mic className="h-3.5 w-3.5 text-slate-500" /> Voice Studio
          </p>
          <p className="text-[10px] text-slate-500">Pick the default voice for new voice campaigns · preview any voice instantly.</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="text-[9px] border border-slate-200 bg-slate-50 text-slate-600">
            {speechAvailable ? `Browser voices: ${voicesReady ? (window.speechSynthesis.getVoices().length) : '…'}` : 'Preview unsupported'}
          </Badge>
          <Button size="sm" variant="outline" className="h-8 text-[11px] gap-1" onClick={stopAll} data-testid="voice-stop-all">
            <Square className="h-3 w-3" /> Stop
          </Button>
        </div>
      </div>

      {/* Default voice card */}
      <div className="rounded-xl border border-slate-200 bg-white p-4 flex items-center justify-between" data-testid="voice-default-card">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-slate-900 inline-flex items-center justify-center">
            <Volume2 className="h-4 w-4 text-white" />
          </div>
          <div>
            <p className="text-[11px] text-slate-500">Current default for voice campaigns</p>
            <p className="text-[14px] font-semibold text-slate-900">{VOICES.find((v) => v.id === defaultId)?.name} <span className="text-[11px] text-slate-400 font-normal">· {VOICES.find((v) => v.id === defaultId)?.desc}</span></p>
          </div>
        </div>
        <Button size="sm" className="h-8 text-[11px] bg-[#111] hover:bg-[#000] gap-1" onClick={() => preview(defaultId)} data-testid="voice-default-preview">
          <Play className="h-3 w-3" /> Preview
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-1.5 flex-wrap" data-testid="voice-filters">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            className={cn('text-[10px] px-2.5 py-1 rounded-full border transition-colors',
              filter === f.id ? 'bg-slate-900 text-white border-slate-900' : 'bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100')}
            style={{ cursor: 'pointer' }}
            data-testid={`voice-filter-${f.id}`}
          >{f.label}</button>
        ))}
      </div>

      {/* Sample sentence */}
      <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid="voice-sample-card">
        <div className="flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5 text-slate-500" />
          <p className="text-[11px] font-semibold text-slate-800">Preview sentence</p>
          <span className="ml-auto text-[9px] text-slate-400">Used for every voice preview below</span>
        </div>
        <textarea
          value={sample}
          onChange={(e) => setSample(e.target.value)}
          rows={2}
          placeholder="Type what the voice should say…"
          className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[12px] outline-none focus:border-slate-900 resize-none"
          data-testid="voice-sample-input"
        />
      </div>

      {/* Voice grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="voice-grid">
        {visibleVoices.map((v) => {
          const isSelected = v.id === voiceId;
          const isDefault = v.id === defaultId;
          const isPlaying = playingId === v.id;
          return (
            <div
              key={v.id}
              onClick={() => setVoiceId(v.id)}
              className={cn('rounded-xl border p-3.5 transition-colors cursor-pointer bg-white',
                isSelected ? 'border-slate-900 shadow-sm' : 'border-slate-200 hover:border-slate-300')}
              data-testid={`voice-card-${v.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-slate-100 inline-flex items-center justify-center">
                    <UserSquare2 className="h-4 w-4 text-slate-500" />
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-slate-900 inline-flex items-center gap-1.5">
                      {v.name}
                      {isDefault && <Badge className="text-[8px] border border-emerald-200 bg-emerald-50 text-emerald-700 px-1.5 py-0 h-4">Default</Badge>}
                    </p>
                    <p className="text-[10px] text-slate-500">{v.desc}</p>
                  </div>
                </div>
                {isSelected && <CheckCircle2 className="h-3.5 w-3.5 text-slate-900" />}
              </div>
              <div className="flex items-center gap-2 mt-3">
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{v.gender}</span>
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{v.accent}</span>
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700">{v.tone}</span>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <Button
                  size="sm"
                  className="h-7 text-[10px] bg-[#111] hover:bg-[#000] gap-1 flex-1"
                  onClick={(e) => { e.stopPropagation(); preview(v.id); }}
                  data-testid={`voice-preview-${v.id}`}
                >
                  {isPlaying ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />}
                  {isPlaying ? 'Playing…' : 'Preview'}
                </Button>
                <Button
                  size="sm"
                  variant={isDefault ? 'default' : 'outline'}
                  className={cn('h-7 text-[10px] gap-1', isDefault && 'bg-slate-900 hover:bg-slate-900 cursor-default')}
                  onClick={(e) => { e.stopPropagation(); if (!isDefault) setAsDefault(v.id); }}
                  data-testid={`voice-set-default-${v.id}`}
                  disabled={isDefault}
                >
                  <Star className={cn('h-3 w-3', isDefault && 'fill-current')} />
                  {isDefault ? 'Default' : 'Set default'}
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Clone-your-voice upsell */}
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 flex items-start gap-3" data-testid="voice-clone-card">
        <div className="h-9 w-9 rounded-md bg-white border border-slate-200 inline-flex items-center justify-center shrink-0">
          <Mic className="h-4 w-4 text-slate-500" />
        </div>
        <div className="flex-1">
          <p className="text-[12px] font-semibold text-slate-800">Clone a human voice for your practice</p>
          <p className="text-[10px] text-slate-500">Record ~3 minutes of a provider or staff voice. Echo uses it for voicemails and confirmations.</p>
        </div>
        <Button size="sm" variant="outline" className="h-8 text-[11px]" onClick={() => toast.info('Coming soon — enterprise only')} data-testid="voice-clone-btn">
          Request access
        </Button>
      </div>
    </div>
  );
};

export default VoiceStudio;
